import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, of, switchMap, tap } from 'rxjs';

import { environment } from '../../environments/environment';
import { LocalStorageService } from './local-storage.service';

export type PermissionsUser = {
	[module_name: string]: {
		access: boolean;
		submodules: {
			name: string;
			access: boolean;
		}[];
	};
};

export type SubRole = {
	action: string;
	id: string;
	module: string;
	submodule: string;
};

export type LoginResponse = {
	accessToken: string;
	refreshToken: string;
	role: string;
	name: string;
	userId: string;
	institutionId: string;
	identifier: string;
	permissions: PermissionsUser;
	subroles: SubRole[];
};
export interface LoginValleLabResponse {
	permissions: Permissions;
	refreshToken: string;
	subRole: string;
	subroles: {
		action: string;
		id: string;
		module: string;
		submodule: string;
	}[];
	token: string;
	userId: string;
}

export interface Permissions {
	hvi: Hvi;
}

export interface Hvi {
	access: boolean;
	submodules: SubModule[];
}
export interface SubModule {
	access: boolean;
	name: string;
}
@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private baseUrl: string = environment.baseUrl;
	private valleLabUrl: string = environment.urlApiValleLab;

	constructor(private ls: LocalStorageService, private http: HttpClient) {}

	verificateAuthentication() {
		const logged = this.ls.getItem('bearer');
		return !!logged;
	}

	login(token: string) {
		const isInstitutionalResumePermissions =
			this.getInstitutionalResumePermissions(token);
		this.ls.saveItem(
			'isHviPermissions',
			isInstitutionalResumePermissions ? 'true' : 'false',
		);

		return this.http
			.post<LoginResponse>(`${this.baseUrl}/auth/login`, {
				app: 'SIGP',
				token,
			})
			.pipe(
				switchMap((loginResponse) => {
					this.handleResponse(loginResponse, this.ls);

					if (isInstitutionalResumePermissions) {
						return this.http
							.post<LoginValleLabResponse>(
								`${this.valleLabUrl}/auth/login`,
								{
									app: 'SIGP',
									token,
								},
								{
									headers: {
										Authorization: `Bearer ${loginResponse.accessToken}`,
									},
								},
							)
							.pipe(
								tap((loginValleLab) => {
									this.valleLabApiResponse(loginValleLab, this.ls);
								}),
							);
					}

					return of(loginResponse);
				}),
				catchError((error) => {
					return of(null); // Return a safe fallback value
				}),
			);
	}

	refreshToken() {
		return this.http
			.get<LoginResponse>(`${this.baseUrl}/auth/refresh`, {
				headers: {
					Authorization: `Bearer ${this.ls.getItem('refresh')}`,
				},
			})
			.pipe(
				switchMap((response) => {
					this.handleResponse(response, this.ls);
					if (this.ls.getItem('isHviPermissions') === 'true') {
						return this.valleLabRefreshToken();
					}

					return of(response);
				}),
			);
	}
	valleLabRefreshToken() {
		return this.http
			.post<LoginValleLabResponse>(`@valleLabApi/auth/refresh`, {
				refreshToken: this.ls.getItem('refreshTokenValleLab'),
			})
			.pipe(
				tap((response) => {
					this.valleLabApiResponse(response, this.ls);
				}),
			);
	}

	logout() {
		return this.http.get(`${this.baseUrl}/auth/logout`).pipe(
			switchMap(() => {
				const token = localStorage.getItem('tokenValleLab');
				if (token) {
					return this.http.get<any>(`@valleLabApi/auth/logout`).pipe(
						tap(() => {
							this.clearSession();
						}),
					);
				}
				this.clearSession();
				return of(null);
			}),
			catchError((error) => {
				return of(null); // Return a safe fallback value
			}),
		);
	}

	clearSession() {
		localStorage.clear();
		this.ls.clear();
	}

	private handleResponse(
		response: LoginResponse,
		localStorage: LocalStorageService,
	) {
		localStorage.saveItem('bearer', response.accessToken);
		localStorage.saveItem('refresh', response.refreshToken);
		localStorage.saveItem('role', response.role);
		localStorage.saveItem('name', response.name);
		localStorage.saveItem('userId', response.userId);
		localStorage.saveItem('identifier', response.identifier);
		localStorage.saveItem('institutionId', response.institutionId);
		localStorage.saveItem('permissions', JSON.stringify(response.permissions));
		localStorage.saveItem('subroles', JSON.stringify(response.subroles));
	}
	private getInstitutionalResumePermissions(token: string): boolean {
		if (!token) return false;

		let decode;
		try {
			const payload = token.split('.')[1];
			if (!payload) return false; // Verificar si el payload existe
			const decodedPayload = window.atob(payload);
			decode = JSON.parse(decodedPayload);
		} catch (e) {
			return false;
		}
		return this.hasAdminRole(decode) || this.hasInstitutionalRoles(decode);
	}

	private hasAdminRole(decode: any): boolean {
		if (decode.hasOwnProperty('roles')) {
			return decode.roles.hasOwnProperty('admin');
		}
		if (decode.hasOwnProperty('role')) {
			const roles = decode.role;
			return roles === 'admin';
		}
		return false;
	}

	private hasInstitutionalRoles(decode: any): boolean {
		return (
			decode.hasOwnProperty('roles') &&
			(decode.roles.hasOwnProperty('secretary') ||
				decode.roles.hasOwnProperty('institution'))
		);
	}
	private valleLabApiResponse(
		loginValleLab: LoginValleLabResponse,
		localStorage: LocalStorageService,
	) {
		const subroles: any[] = JSON.parse(this.ls.getItem('subroles', '[]')) || [];
		let permissions = JSON.parse(this.ls.getItem('permissions', '{}'));
		const nonHviSubRoles = subroles.filter(
			(subRole) => subRole.module !== 'hvi',
		);

		const hviSubRoles = loginValleLab.subroles;

		this.ls.saveItem('hviRole', loginValleLab.subRole);
		const dashboardHviSubrule: {
			action: string;
			id: string;
			module: string;
			submodule: string;
		} = {
			action: 'view_dashboard',
			id: '4c2d8f9a-5171-4fa1-9ef9-5ab9c728db9f',
			module: 'hvi',
			submodule: 'dashboard',
		};
		hviSubRoles.push(dashboardHviSubrule);
		const hviPermissions: Hvi = {
			access: loginValleLab.permissions.hvi.access,
			submodules: [
				...loginValleLab.permissions.hvi.submodules,
				{
					access: loginValleLab.permissions.hvi.access,
					name: 'dashboard',
				},
				{
					name: 'pei',
					access: true,
				},
			],
		};

		permissions['institutional-resume'] = hviPermissions;
		const subRoles = [...nonHviSubRoles, ...hviSubRoles];

		localStorage.saveItem('tokenValleLab', loginValleLab.token);
		localStorage.saveItem('refreshTokenValleLab', loginValleLab.refreshToken);

		localStorage.saveItem('subroles', JSON.stringify(subRoles));
		localStorage.saveItem('permissions', JSON.stringify(permissions));
	}
}
